import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

// RP10 (D10 C) — code de vérification demandé pour un acte engageant (envoyer une
// offre, contester un compte rendu) quand la session a été ouverte par un lien.
// Le serveur répond 428 { detail: { code: 'verification_requise' | 'code_incorrect' | … } }.
export const CODE_HEADER = 'X-Code-Verification';

export function verificationRequise(err) {
  return err?.response?.status === 428;
}

export function messageVerification(err) {
  const d = err?.response?.data?.detail;
  return d && typeof d === 'object' ? d.message || '' : '';
}

/**
 * props :
 * - open, onOpenChange
 * - demanderUrl : URL (POST) qui envoie le code ; demanderBody : corps éventuel
 * - titre : ex. « Confirmer l'envoi de votre offre »
 * - onValider(code) : relance l'action avec l'en-tête X-Code-Verification ; doit
 *   renvoyer une promesse rejetée (erreur axios) si le code est refusé.
 */
export default function CodeVerificationDialog({ open, onOpenChange, demanderUrl, demanderBody, titre, onValider }) {
  const [envoi, setEnvoi] = useState(null); // { canal, destination }
  const [code, setCode] = useState('');
  const [erreur, setErreur] = useState('');
  const [chargement, setChargement] = useState(false);
  const demandeFaite = useRef(false);

  const demander = async () => {
    setErreur('');
    setChargement(true);
    try {
      const { data } = await axios.post(demanderUrl, demanderBody || {}, { withCredentials: true });
      setEnvoi(data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setErreur(typeof d === 'string' ? d : "Le code n'a pas pu être envoyé. Réessayez.");
    } finally {
      setChargement(false);
    }
  };

  useEffect(() => {
    if (open && !demandeFaite.current) {
      demandeFaite.current = true;
      demander();
    }
    if (!open) {
      demandeFaite.current = false;
      setEnvoi(null);
      setCode('');
      setErreur('');
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const valider = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) {
      setErreur('Le code comporte 6 chiffres.');
      return;
    }
    setChargement(true);
    setErreur('');
    try {
      await onValider(code);
      onOpenChange(false);
    } catch (err) {
      setErreur(messageVerification(err) || (typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : 'Code refusé.'));
    } finally {
      setChargement(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="code-verification-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>{titre || 'Code de vérification'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={valider} className="space-y-3">
          <p className="text-sm text-[#4A4A4A]">
            {envoi
              ? (envoi.canal === 'sms'
                ? `Nous venons d'envoyer un code par SMS au ${envoi.destination}.`
                : `Nous venons d'envoyer un code par e-mail à ${envoi.destination}.`)
              : 'Envoi du code…'}
          </p>
          <Input
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            placeholder="Code à 6 chiffres"
            className="h-12 rounded-none text-center tracking-widest text-lg"
            data-testid="code-verification-input"
          />
          {erreur && <p className="text-sm text-[#8B3A3A]">{erreur}</p>}
          <div className="flex gap-2">
            <Button type="button" variant="outline" className="flex-1 rounded-none" onClick={demander} disabled={chargement}>
              Renvoyer le code
            </Button>
            <Button type="submit" className="flex-1 rounded-none bg-[#2C2C2C] hover:bg-[#4A4A4A]" disabled={chargement} data-testid="code-verification-submit">
              Valider
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
